import { Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { EChartComponent } from 'pages/Tenant/AroDashboard/GaugeChart';
import { useGetChartViewDetails } from 'services/v1/SystemTenant/AROChart/ChartViewDefinitionService';
import { useGetCubeDefinitionDetails } from 'services/v1/SystemTenant/AROCube/CubeBuilderService';
import { useGetChartItemData } from 'services/v1/Tenant/AROChart/ChartViewService';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import eventEmitter, { EVENT_EMITTER_KEYS } from 'utils/EventEmitter';
import { ChartType, CHART_DISPLAY_CONFIG } from 'constant/ChartViewConstant';
import { getChartIconByType, getChartLabelByType } from 'pages/SystemTenant/AROChart/ChartDefinition/components/ChartItemComponent';
import TouchAppOutlinedIcon from '@mui/icons-material/TouchAppOutlined';
import NorthOutlinedIcon from '@mui/icons-material/NorthOutlined';

// ── Shared card styles ──

const cardBaseSx = {
  width: '100%',
  height: '100%',
  bgcolor: '#ffffff',
  border: '1px solid #eef0f4',
  borderRadius: 2,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const headerBarSx = {
  px: 1.5,
  py: 0.75,
  borderBottom: '1px solid #f0f2f5',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 0.75,
  flexShrink: 0,
  minHeight: 36,
};

// ── 2D: Chart-type skeleton loaders ──

function BarChartSkeleton() {
  const bars = [55, 75, 40, 90, 60, 80, 45];
  return (
    <Stack direction='row' alignItems='flex-end' justifyContent='center' gap='6px' height='100%' width='100%' p={3}>
      {bars.map((h, i) => (
        <Skeleton
          key={i}
          variant='rectangular'
          animation='wave'
          sx={{ flex: 1, maxWidth: 28, height: `${h}%`, borderRadius: '3px 3px 0 0' }}
        />
      ))}
    </Stack>
  );
}

function LineChartSkeleton() {
  return (
    <Stack height='100%' width='100%' p={3} justifyContent='center'>
      <Skeleton variant='rectangular' animation='wave' sx={{ width: '100%', height: '70%', borderRadius: 1 }} />
      <Stack direction='row' justifyContent='space-between' mt={1}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant='text' animation='wave' sx={{ width: 40 }} />
        ))}
      </Stack>
    </Stack>
  );
}

function PieChartSkeleton() {
  return (
    <Stack height='100%' width='100%' alignItems='center' justifyContent='center' p={3}>
      <Skeleton variant='circular' animation='wave' sx={{ width: 100, height: 100 }} />
      <Stack direction='row' gap={1} mt={2}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant='rounded' animation='wave' sx={{ width: 50, height: 12 }} />
        ))}
      </Stack>
    </Stack>
  );
}

function GaugeChartSkeleton() {
  return (
    <Stack height='100%' width='100%' alignItems='center' justifyContent='center' p={3}>
      <Skeleton
        variant='circular'
        animation='wave'
        sx={{ width: 110, height: 110, clipPath: 'inset(0 0 40% 0)' }}
      />
      <Skeleton variant='text' animation='wave' sx={{ width: 60, mt: -3 }} />
    </Stack>
  );
}

function ChartSkeletonByType({ chartType }: { chartType?: string }) {
  switch (chartType) {
    case ChartType.BAR:
    case ChartType.ROW:
      return <BarChartSkeleton />;
    case ChartType.LINE:
      return <LineChartSkeleton />;
    case ChartType.PIE:
      return <PieChartSkeleton />;
    case ChartType.GAUGE:
      return <GaugeChartSkeleton />;
    default:
      return <ActivityIndicator />;
  }
}

// ── Main Component ──

export function ChartItem(props: {
  chartItemId: string;
  memberIds: string[];
  cubeAttributeId: string;
  accumulationType: string;
  insightViewAttributeIds?: string[];
}) {
  const [searchParams] = useSearchParams();
  const myViewId = searchParams.get('myViewId') || undefined;
  const { chartViewId } = useParams<{ chartViewId: string }>();
  const { data: chartViewDefintition } = useGetChartViewDetails(chartViewId);
  const { data: cubeDefinitionDetails } = useGetCubeDefinitionDetails(
    chartViewDefintition?.data.cube?.value?.toString()
  );

  const kTreeName = useMemo(() => {
    if (cubeDefinitionDetails?.data) {
      return cubeDefinitionDetails?.data.member.label;
    }
    return null;
  }, [cubeDefinitionDetails?.data]);

  const { chartItemId, memberIds, cubeAttributeId, accumulationType, insightViewAttributeIds } = props;
  const [chartFilters, setChartFilters] = useState<{ [key: string]: AutoCompleteItem }>({});
  // 2F: Interaction feedback state
  const [isUpdating, setIsUpdating] = useState(false);

  const { data: chartDisplayData, isLoading: isLoadingChart } = useGetChartItemData({
    chartViewItemId: chartItemId,
    memberIds,
    cubeAttributeId,
    accumulationType,
    insightViewAttributeIds,
    filters: Object.values(chartFilters),
    myViewId,
  });

  const chartType = chartDisplayData?.data?.chartViewItem?.chartType;
  const chartName = chartDisplayData?.data?.chartViewItem?.name || 'Chart';

  // Active filter chips
  const activeFilters = useMemo(() => {
    return Object.entries(chartFilters).map(([key, val]) => ({
      key,
      label: `${key}: ${val.value}`,
    }));
  }, [chartFilters]);

  const handleOnChartFilterChange = (filterName: string, item: AutoCompleteItem) => {
    setChartFilters((prev) => ({
      ...prev,
      [filterName]: {
        value: item.value,
        label: filterName,
      },
    }));
  };

  const handleOnResponseLineChartClick = (timePeriodName: string) => {
    if (
      chartDisplayData?.data.chartViewItem.chartType === ChartType.GAUGE ||
      chartDisplayData?.data.chartViewItem.chartType === ChartType.PIE
    ) {
      // 2F: Flash feedback on receiving cross-chart event
      setIsUpdating(true);
      setChartFilters((prev) => ({
        ...prev,
        timePeriod: {
          value: timePeriodName,
          label: 'timePeriod',
        },
      }));
      setTimeout(() => setIsUpdating(false), 600);
    }
  };

  const handleOnLineChartClick = (timePeriodName: string) => {
    eventEmitter.emit(EVENT_EMITTER_KEYS.TRIGGER_CHART_TIME_PERIOD_FILTER_CHANGE, timePeriodName);
  };

  const handleClearFilter = (key: string) => {
    setChartFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  useEffect(() => {
    eventEmitter.on(EVENT_EMITTER_KEYS.TRIGGER_CHART_TIME_PERIOD_FILTER_CHANGE, handleOnResponseLineChartClick);

    return () => {
      eventEmitter.off(EVENT_EMITTER_KEYS.TRIGGER_CHART_TIME_PERIOD_FILTER_CHANGE, handleOnResponseLineChartClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDisplayData?.data.chartViewItem.chartType]);

  // ── 2C: "Select Member" Empty State ──
  if (memberIds.length === 0) {
    return (
      <Box sx={cardBaseSx}>
        {CHART_DISPLAY_CONFIG.SHOW_CHART_HEADER && (
          <Box sx={headerBarSx}>
            {chartType && getChartIconByType(chartType)}
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#333' }}>{chartName}</Typography>
          </Box>
        )}
        <Stack flex={1} alignItems='center' justifyContent='center' gap={1} p={2}>
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: '#f0faf6',
              mb: 0.5,
            }}
          >
            <TouchAppOutlinedIcon sx={{ fontSize: 28, color: '#42BB93' }} />
          </Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555', textAlign: 'center' }}>
            Select {kTreeName || 'a member'} to view this chart
          </Typography>
          <Stack direction='row' alignItems='center' gap={0.5}>
            <NorthOutlinedIcon sx={{ fontSize: 14, color: '#aaa' }} />
            <Typography sx={{ fontSize: 11, color: '#aaa', textAlign: 'center' }}>
              Use the filter panel above
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // ── 2D: Loading State with chart-type skeleton ──
  if (isLoadingChart) {
    return (
      <Box sx={cardBaseSx}>
        {CHART_DISPLAY_CONFIG.SHOW_CHART_HEADER && (
          <Box sx={headerBarSx}>
            <Skeleton variant='circular' width={20} height={20} />
            <Skeleton variant='text' width={100} />
          </Box>
        )}
        <Box flex={1}>
          <ChartSkeletonByType chartType={chartType} />
        </Box>
      </Box>
    );
  }

  // ── Rendered chart ──
  const isClickable = chartType === ChartType.LINE;

  return (
    <Box
      sx={{
        ...cardBaseSx,
        // 2F: Update pulse animation
        transition: 'box-shadow 0.3s ease',
        boxShadow: isUpdating ? '0 0 0 3px rgba(66,187,147,0.3)' : 'none',
        animation: isUpdating ? 'chart-update-pulse 0.6s ease' : 'none',
        '@keyframes chart-update-pulse': {
          '0%': { boxShadow: '0 0 0 0 rgba(66,187,147,0.4)' },
          '50%': { boxShadow: '0 0 0 6px rgba(66,187,147,0.15)' },
          '100%': { boxShadow: '0 0 0 0 rgba(66,187,147,0)' },
        },
      }}
    >
      {/* ── Card header ── */}
      {CHART_DISPLAY_CONFIG.SHOW_CHART_HEADER && (
        <Box sx={headerBarSx}>
          {chartType && getChartIconByType(chartType)}
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#333', flex: 1 }}>{chartName}</Typography>
          {chartType && (
            <Typography sx={{ fontSize: 10, color: '#aaa', textTransform: 'uppercase', fontWeight: 600 }}>
              {getChartLabelByType(chartType)}
            </Typography>
          )}
          {isClickable && (
            <TouchAppOutlinedIcon sx={{ fontSize: 14, color: '#bbb', ml: 0.5 }} />
          )}
        </Box>
      )}

      {/* ── Active filter chips ── */}
      {activeFilters.length > 0 && (
        <Stack direction='row' gap={0.5} px={1.5} py={0.5} flexWrap='wrap' sx={{ borderBottom: '1px solid #f0f2f5' }}>
          {activeFilters.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              size='small'
              onDelete={() => handleClearFilter(f.key)}
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Stack>
      )}

      {/* ── Chart body ── */}
      <Box
        flex={1}
        sx={{
          ...(isClickable && { cursor: 'crosshair' }),
          minHeight: 0,
        }}
      >
        {chartDisplayData?.data && (
          <EChartComponent
            chartDisplayData={chartDisplayData?.data}
            key={chartItemId}
            onFilterChange={handleOnChartFilterChange}
            onLineChartDataPointClick={handleOnLineChartClick}
          />
        )}
        {!chartDisplayData?.data && (
          <Stack flex={1} height='100%' alignItems='center' justifyContent='center' p={2}>
            <Typography sx={{ fontSize: 12, color: '#aaa' }}>No data available</Typography>
            <Typography sx={{ fontSize: 11, color: '#ccc', mt: 0.5, textAlign: 'center' }}>
              Try adjusting your member or measurement selection.
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
